/**
 * Global contact form handler:
 * - Explicit .custom-api-form (contact + quote pages)
 * - Auto-capture any Shopify {% form 'contact' %} or action="/contact" forms on the page
 */
(function () {
  const EXPLICIT_SELECTOR = 'form.custom-api-form';
  const BOUND_FLAG = 'customApiBound';
  const REDIRECT_PARAM_KEYS = [
    'contact_error',
    'contact_error_msg',
    'contact_submitted',
    'quote_error',
    'quote_error_msg',
    'quote_submitted',
  ];

  function cleanPageUrl(href) {
    try {
      const url = new URL(href, window.location.origin);
      REDIRECT_PARAM_KEYS.forEach(function (key) {
        url.searchParams.delete(key);
      });
      return url.toString();
    } catch (_e) {
      return href;
    }
  }

  function stripContactRedirectParamsFromLocation() {
    try {
      const url = new URL(window.location.href);
      let changed = false;
      REDIRECT_PARAM_KEYS.forEach(function (key) {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        history.replaceState(null, '', url.pathname + url.search + url.hash);
      }
    } catch (_e) {
      /* noop */
    }
  }

  function syncPageUrlFields() {
    const clean = cleanPageUrl(window.location.href);
    document.querySelectorAll('input[name="page_url"]').forEach(function (input) {
      input.value = clean;
    });
  }

  function ensurePageUrlField(form) {
    let input = form.querySelector('input[name="page_url"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'page_url';
      form.appendChild(input);
    }
    input.value = cleanPageUrl(window.location.href);
    return input;
  }

  function globalApiUrl() {
    return String(window.BH_CONTACT_SUBMISSIONS_API_URL || '').trim();
  }

  function globalSiteKey() {
    return String(window.BH_RECAPTCHA_SITE_KEY || '').trim();
  }

  function recaptchaAction(source) {
    if (source === 'quote_request') return 'quote_request';
    if (source === 'contact_page') return 'contact_page';
    return 'contact_form';
  }

  function getChipPopupEl(form) {
    return form.closest('.chip-popup') || form.querySelector('.chip-popup');
  }

  function isChipPopupForm(form) {
    return !!getChipPopupEl(form);
  }

  function inferSource(form) {
    const explicit = (form.getAttribute('data-source') || '').trim();
    if (explicit) return explicit;

    if (form.id === 'contact-page-form') return 'contact_page';
    if (form.id === 'supplier-onboarding-form' || form.id === 'SupplierForm') {
      return 'supplier_onboarding';
    }
    if (form.id === 'qrf-form' || form.closest('#qrf-root')) return 'quote_request';
    if (form.classList.contains('dl-coa-shopify-form')) return 'document_library';
    if (form.id === 'ask-scientist-form' || form.closest('#ask-scientist-popup, #ask-scientist')) {
      return 'ask_scientist';
    }
    if (form.closest('#talk-to-scientist-popup, #talk-to-scientist')) return 'talk_to_scientist';
    if (form.id === 'contact_form') return 'services_hub';
    if (form.closest('.request-quote-popup, .promo-modal')) return 'quote_modal';

    return 'contact_form';
  }

  function isNewsletterForm(form) {
    return !!form.querySelector('input[name="contact[tags]"][value="newsletter"]');
  }

  function isShopifyContactForm(form) {
    if (form.dataset[BOUND_FLAG] === '1') return false;
    if (form.dataset.skipCustomApi === '1') return false;
    if (isNewsletterForm(form)) return false;

    if (form.classList.contains('custom-api-form')) return true;

    if (form.querySelector('input[name="form_type"][value="contact"]')) return true;

    const action = (form.getAttribute('action') || '').toLowerCase();
    if (action.indexOf('/contact') !== -1) return true;

    return false;
  }

  function findFormsToBind() {
    const found = new Set();

    document.querySelectorAll(EXPLICIT_SELECTOR).forEach(function (f) {
      found.add(f);
    });

    document.querySelectorAll('form').forEach(function (form) {
      if (isShopifyContactForm(form)) found.add(form);
    });

    return Array.from(found);
  }

  function ensureRecaptchaField(form) {
    if (form.querySelector('[data-recaptcha-token]')) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'g-recaptcha-response';
    input.setAttribute('data-recaptcha-token', '');
    input.value = '';
    form.appendChild(input);
  }

  function resolveApiUrl(form) {
    return (
      globalApiUrl() ||
      (form.getAttribute('data-api-url') || '').trim() ||
      (form.dataset.bhContactApiUrl || '').trim()
    );
  }

  function prepareForm(form) {
    const apiUrl = resolveApiUrl(form);
    if (!apiUrl) return false;

    form.dataset.bhContactApiUrl = apiUrl;

    const existingAction = (form.getAttribute('action') || '').trim();
    if (!form.dataset.bhShopifyContactAction) {
      if (existingAction && existingAction !== '#' && existingAction.indexOf('/contact') !== -1) {
        form.dataset.bhShopifyContactAction = existingAction;
      } else {
        form.dataset.bhShopifyContactAction = '/contact#contact_form';
      }
    }

    // Keep navigation on the storefront; API + Shopify relay are called via fetch only.
    form.setAttribute('action', '#');
    form.setAttribute('method', 'post');
    form.setAttribute('data-form-wrap', '');
    if (!form.getAttribute('data-source')) {
      form.setAttribute('data-source', inferSource(form));
    }
    ensureRecaptchaField(form);

    ensurePageUrlField(form);

    if (!form.querySelector('input[name="source"]')) {
      const source = document.createElement('input');
      source.type = 'hidden';
      source.name = 'source';
      source.value = inferSource(form);
      form.appendChild(source);
    }

    return true;
  }

  function getConfig(form) {
    const source = inferSource(form);
    return {
      apiUrl: resolveApiUrl(form),
      siteKey: (form.getAttribute('data-recaptcha-site-key') || globalSiteKey()).trim(),
      source: source,
      successHeading: form.getAttribute('data-success-heading') || 'Thank you',
      successText:
        form.getAttribute('data-success-text') ||
        'Your message has been sent successfully. We will respond as soon as possible.',
      successParam: form.getAttribute('data-success-param') || '',
      root: form.closest('[data-form-root]') || document.getElementById('qrf-root') || form.parentElement,
      form: form,
      isDlCoa: isDlCoaForm(form),
      isQuoteModal: !!form.closest('#quote-modal'),
      isModal: !!form.closest(
        '.chip-popup, .dialog, [role="dialog"], .promo-modal-overlay, .dl-coa-overlay, #quote-modal'
      ),
      shopifyContact: shopifyContactRelayEnabled(form),
    };
  }

  function isShopifyStorefront() {
    return typeof window.Shopify !== 'undefined' && !!window.Shopify.shop;
  }

  function shopifyContactRelayEnabled(form) {
    if (!isShopifyStorefront()) return false;
    if (window.BH_SHOPIFY_CONTACT_RELAY === '0' || window.BH_SHOPIFY_CONTACT_RELAY === false) {
      return false;
    }
    if (form.getAttribute('data-bh-shopify-contact') === '0') return false;
    return true;
  }

  function shopifyContactEndpoint(form) {
    const stored = (form.dataset.bhShopifyContactAction || '').trim();
    if (stored) {
      return stored.split('#')[0] || '/contact';
    }
    const action = (form.getAttribute('action') || '').trim();
    if (action && action !== '#' && action.indexOf('/contact') !== -1) {
      return action.split('#')[0] || '/contact';
    }
    return '/contact';
  }

  function ensureShopifyContactBasics(fd) {
    if (!fd.has('form_type')) {
      fd.append('form_type', 'contact');
    }
    if (!fd.has('utf8')) {
      fd.append('utf8', '✓');
    }
  }

  function appendShopifyExtras(fd, body, source) {
    const lines = [];
    const labels = {
      organization: 'Organization',
      role: 'Role',
      topic: 'Topic',
      order_reference: 'Order / RFQ #',
      phone: 'Phone',
      country: 'Country',
      page_url: 'Page URL',
      catalog_size: 'Catalog size',
      partnership_tier: 'Partnership tier',
    };

    if (source) {
      lines.push('Form source: ' + source);
    }

    Object.keys(labels).forEach(function (key) {
      const value = String(body[key] || '').trim();
      if (value !== '') {
        lines.push(labels[key] + ': ' + value);
      }
    });

    if (lines.length === 0) {
      return;
    }

    const prefix = lines.join('\n') + '\n\n';
    const existing = String(fd.get('contact[body]') || '').trim();
    fd.set('contact[body]', prefix + (existing || '(No message)'));
  }

  function buildShopifyContactFormData(form, source) {
    const fd = new FormData();
    const body = buildJsonBody(form, source);
    const skip = new Set([
      'source',
      'page_url',
      'g-recaptcha-response',
      'recaptcha_token',
      'website',
      'form_type',
      'utf8',
    ]);
    const hasShopifyFields =
      !!form.querySelector('input[name="form_type"][value="contact"]') ||
      !!form.querySelector('[name="contact[email]"]');

    if (hasShopifyFields) {
      const raw = new FormData(form);
      raw.forEach(function (value, key) {
        if (skip.has(key)) return;
        if (key === 'contact[website]') return;
        fd.append(key, value);
      });
      ensureShopifyContactBasics(fd);

      if (!fd.has('contact[body]') && body.message) {
        fd.append('contact[body]', body.message);
      }
      if (!fd.has('contact[name]') && body.name) {
        fd.append('contact[name]', body.name);
      }
      if (!fd.has('contact[email]') && body.email) {
        fd.append('contact[email]', body.email);
      }

      appendShopifyExtras(fd, body, source);
      return fd;
    }

    ensureShopifyContactBasics(fd);
    fd.append('contact[name]', String(body.name || 'Unknown').trim() || 'Unknown');
    fd.append('contact[email]', String(body.email || '').trim());

    const shopBody = String(body.message || body.body || '').trim() || '(No message)';
    fd.append('contact[body]', shopBody);
    appendShopifyExtras(fd, body, source);

    if (body.phone) fd.append('contact[phone]', String(body.phone));
    if (body.topic) fd.append('contact[Topic]', String(body.topic));
    if (body.organization) fd.append('contact[Organization]', String(body.organization));

    return fd;
  }

  function postToShopifyContact(form, cfg) {
    if (!cfg.shopifyContact) {
      return Promise.resolve({ skipped: true });
    }

    const url = shopifyContactEndpoint(form);
    const fd = buildShopifyContactFormData(form, cfg.source);

    return fetch(url, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      redirect: 'manual',
      headers: {
        Accept: 'text/html,application/xhtml+xml',
      },
    }).then(function (res) {
      if (res.type === 'opaqueredirect' || res.status === 0) {
        return { ok: true };
      }
      if (res.status >= 200 && res.status < 400) {
        return { ok: true };
      }
      console.warn('BioHippo: Shopify contact relay HTTP ' + res.status);
      return { ok: false, status: res.status };
    });
  }

  function isDlCoaForm(form) {
    return form.classList.contains('dl-coa-shopify-form');
  }

  function dlCoaSetLoading(form, loading) {
    const overlay = form.closest('.dl-coa-overlay');
    if (!overlay) return;

    const loader = overlay.querySelector('.dl-coa-submit-loader');
    const btn = form.querySelector('.dl-coa-submit-btn');

    if (loader) {
      loader.classList.toggle('is-visible', loading);
      loader.setAttribute('aria-hidden', loading ? 'false' : 'true');
    }
    if (btn) {
      btn.disabled = loading;
      if (loading) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    }
  }

  function showDlCoaSuccess(form) {
    dlCoaSetLoading(form, false);

    let success = form.querySelector('.dl-coa-api-success');
    if (!success) {
      success = document.createElement('p');
      success.className = 'dl-coa-api-success form-success';
      success.style.cssText =
        'margin:0 28px 16px;color:green;font-weight:600;font-size:14px;';
      form.insertBefore(success, form.firstChild);
    }
    success.textContent = '✔ Thank you! Your CoA request has been sent successfully.';
    success.hidden = false;

    form.querySelectorAll('.custom-form-error').forEach(function (el) {
      el.hidden = true;
    });
    form.querySelectorAll('.modal-body, .modal-footer').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function syncProductTypes(root) {
    if (!root) return;
    const summary = root.querySelector('#qrf-product-types-field');
    const boxes = root.querySelectorAll('.js-qrf-product-type-cb');
    if (!summary || !boxes.length) return;

    const parts = [];
    boxes.forEach(function (cb) {
      if (cb.checked) parts.push(cb.value);
    });
    summary.value = parts.join('; ');
  }

  function showError(form, message) {
    let box = form.querySelector('.custom-form-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'custom-form-error';
      box.setAttribute('role', 'alert');
      box.style.cssText =
        'color:#b00020;font-weight:600;margin-bottom:12px;padding:12px 16px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;';
      form.prepend(box);
    }
    box.textContent = message;
    box.hidden = false;
  }

  function clearError(form) {
    const box = form.querySelector('.custom-form-error');
    if (box) box.hidden = true;
  }

  /** Restore chip-popup modal form after submit (menu Ask / Talk to Scientist). */
  function resetChipPopupForm(modalEl) {
    if (!modalEl) return;
    const popup =
      modalEl.classList && modalEl.classList.contains('chip-popup')
        ? modalEl
        : modalEl.querySelector
          ? modalEl.querySelector('.chip-popup')
          : null;
    if (!popup) return;
    const form = popup.querySelector('form');
    if (!form) return;

    form.classList.remove('is-submitting');

    popup.querySelectorAll('.custom-form-error, .custom-form-success-inline').forEach(function (el) {
      el.remove();
    });

    form.querySelectorAll('.field, .field-row, .contact-footer, h2, p').forEach(function (el) {
      if (el.closest('.custom-form-success-inline')) return;
      el.style.display = '';
      el.hidden = false;
    });

    const loader = popup.querySelector('.bh-form-loader');
    if (loader) {
      loader.classList.remove('is-visible');
      loader.setAttribute('aria-hidden', 'true');
    }

    setSubmitting(form, false, { isModal: true, isDlCoa: false, isQuoteModal: false });
  }

  function initChipPopupReset() {
    document.addEventListener('click', function (e) {
      const opener = e.target.closest('[data-modal-opener]');
      if (opener && opener.dataset.modal) {
        const modal = document.querySelector(opener.dataset.modal);
        if (modal) resetChipPopupForm(modal);
        return;
      }

      if (e.target.closest('[data-spec-close]') || e.target.classList.contains('overlay')) {
        document.querySelectorAll('.chip-popup').forEach(resetChipPopupForm);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.chip-popup').forEach(resetChipPopupForm);
      }
    });
  }

  window.BH_resetContactModal = resetChipPopupForm;

  function showServicesHubSuccess(cfg) {
    const form = cfg.form;
    const modal = form.closest('#quote-modal');

    let inline = form.querySelector('.custom-form-success-inline');
    if (!inline) {
      inline = document.createElement('div');
      inline.className = 'custom-form-success-inline';
      inline.style.cssText =
        'color:#047857;font-weight:600;padding:12px 0;margin-bottom:8px;font-size:15px;';
      form.prepend(inline);
    }
    inline.textContent = '✔ ' + cfg.successText;
    inline.hidden = false;

    form.querySelectorAll('.form-group').forEach(function (el) {
      el.style.display = 'none';
    });
    if (modal) {
      const footer = modal.querySelector('.modal-footer');
      if (footer) footer.style.display = 'none';
    }

    const pageOk = document.getElementById('successMessage');
    const pageErr = document.getElementById('errorMessage');
    if (pageOk) pageOk.classList.add('show');
    if (pageErr) pageErr.classList.remove('show');

    if (typeof window.closeQuoteModal === 'function') {
      window.setTimeout(function () {
        window.closeQuoteModal();
        form.querySelectorAll('.form-group').forEach(function (el) {
          el.style.display = '';
        });
        if (modal) {
          const footer = modal.querySelector('.modal-footer');
          if (footer) footer.style.display = '';
        }
        if (inline) inline.remove();
        form.reset();
      }, 2200);
    }
  }

  function showSuccess(cfg) {
    const form = cfg.form;

    if (cfg.isQuoteModal) {
      showServicesHubSuccess(cfg);
      return;
    }

    if (cfg.isModal) {
      const popup = cfg.chipPopup || getChipPopupEl(form);
      const host = popup ? popup.querySelector('.dialog .body') || popup : form;
      let inline = host.querySelector('.custom-form-success-inline');
      if (!inline) {
        inline = document.createElement('div');
        inline.className = 'custom-form-success-inline';
        inline.setAttribute('role', 'status');
        inline.style.cssText =
          'background:#ecfdf5;border:1px solid #6ee7b7;border-radius:10px;color:#047857;' +
          'font-weight:600;padding:16px 18px;margin-bottom:12px;font-size:15px;line-height:1.5;';
        host.insertBefore(inline, host.firstChild);
      }
      inline.textContent = '✔ ' + cfg.successText;
      inline.hidden = false;
      form.querySelectorAll('.field, .field-row, .contact-footer, .qrf-card, .qrf-footer').forEach(function (el) {
        el.style.display = 'none';
      });
      if (popup) {
        const h2 = form.querySelector('h2');
        const intro = form.querySelector('p');
        if (h2) h2.style.display = 'none';
        if (intro) intro.style.display = 'none';
      }
      return;
    }

    const root = cfg.root;
    const formWrap = root && root.querySelector('[data-form-wrap]');
    let success =
      root &&
      root.querySelector('.custom-form-success-panel, .qrf-success-panel');

    if (!success && root) {
      success = document.createElement('div');
      success.className = 'custom-form-success-panel qrf-success-panel';
      success.style.cssText =
        'background:#ecfdf5;border:1px solid #6ee7b7;border-radius:12px;padding:2rem;text-align:center;margin-bottom:1rem;';
      success.innerHTML =
        '<h3 style="margin:0 0 0.5rem;font-family:Barlow,sans-serif;color:#0d3540;"></h3><p style="margin:0;color:#4b5563;"></p>';
      if (formWrap) {
        formWrap.insertAdjacentElement('beforebegin', success);
      } else {
        root.prepend(success);
      }
    }

    if (success) {
      success.querySelector('h3').textContent = cfg.successHeading;
      success.querySelector('p').textContent = cfg.successText;
      success.hidden = false;
    }

    if (formWrap) formWrap.hidden = true;
    else form.hidden = true;

    if (success) success.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function injectLoaderStyles() {
    if (document.getElementById('bh-form-loader-styles')) return;
    const style = document.createElement('style');
    style.id = 'bh-form-loader-styles';
    style.textContent =
      '.bh-form-loader-host{position:relative;}' +
      '.bh-form-loader{display:none;position:absolute;inset:0;z-index:40;background:rgba(255,255,255,0.82);' +
      'align-items:center;justify-content:center;border-radius:inherit;}' +
      '.bh-form-loader.is-visible{display:flex;}' +
      '.bh-form-loader-inner{display:flex;flex-direction:column;align-items:center;gap:10px;' +
      'padding:20px 28px;background:#fff;border:1px solid #e2e8ec;border-radius:12px;' +
      'box-shadow:0 8px 32px rgba(13,53,64,0.12);}' +
      '.bh-form-loader-inner svg{width:40px;height:40px;color:#00a2a0;animation:bh-form-spin 0.7s linear infinite;}' +
      '.bh-form-loader-text{font-family:system-ui,sans-serif;font-size:15px;font-weight:600;color:#0d3540;}' +
      '@keyframes bh-form-spin{to{transform:rotate(360deg);}}' +
      'form.is-submitting .bh-form-loader.is-visible{display:flex;}' +
      '.bh-btn-spinner{display:inline-block;width:14px;height:14px;margin-right:8px;vertical-align:-2px;' +
      'border:2px solid rgba(255,255,255,0.35);border-top-color:#fff;border-radius:50%;animation:bh-form-spin 0.65s linear infinite;}';
    document.head.appendChild(style);
  }

  function getLoaderHost(form, cfg) {
    if (cfg && cfg.isQuoteModal) {
      return document.getElementById('quote-modal') || form.closest('.modal') || form;
    }
    if (cfg && cfg.isDlCoa) {
      return form.closest('.dl-coa-overlay') || form;
    }
    if (cfg && cfg.chipPopup) {
      return cfg.chipPopup.querySelector('.dialog') || cfg.chipPopup;
    }
    if (cfg && cfg.isModal) {
      return form.closest('.chip-popup, .dialog, [role="dialog"], .promo-modal-overlay, .modal') || form;
    }
    return (
      form.closest('[data-form-wrap]') ||
      form.closest('#qrf-root') ||
      form.closest('.contact-form-wrap') ||
      form
    );
  }

  function ensureFormLoader(host) {
    if (!host) return null;
    host.classList.add('bh-form-loader-host');
    let loader = host.querySelector(':scope > .bh-form-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'bh-form-loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.setAttribute('aria-live', 'polite');
      loader.innerHTML =
        '<div class="bh-form-loader-inner">' +
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2.5" opacity="0.2"/>' +
        '<path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
        '</svg>' +
        '<span class="bh-form-loader-text">Sending request…</span>' +
        '</div>';
      host.appendChild(loader);
    }
    return loader;
  }

  function getSubmitButtons(form) {
    const buttons = [];
    form.querySelectorAll('button[type="submit"], .qrf-submit, input[type="submit"]').forEach(function (b) {
      buttons.push(b);
    });
    if (form.id) {
      document.querySelectorAll('[form="' + form.id + '"][type="submit"]').forEach(function (b) {
        if (buttons.indexOf(b) === -1) buttons.push(b);
      });
    }
    return buttons;
  }

  function setSubmitting(form, on, cfg) {
    const buttons = getSubmitButtons(form);
    buttons.forEach(function (btn) {
      btn.disabled = on;
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
      if (on) {
        if (!btn.dataset.bhSubmitLabel) {
          btn.dataset.bhSubmitLabel = btn.tagName === 'INPUT' ? btn.value : btn.textContent;
        }
        const spin = '<span class="bh-btn-spinner" aria-hidden="true"></span>';
        if (btn.tagName === 'INPUT') {
          btn.value = 'Sending…';
        } else if (!btn.querySelector('.bh-btn-spinner')) {
          btn.innerHTML = spin + 'Sending…';
        }
      } else if (btn.dataset.bhSubmitLabel) {
        if (btn.tagName === 'INPUT') {
          btn.value = btn.dataset.bhSubmitLabel;
        } else {
          btn.textContent = btn.dataset.bhSubmitLabel;
        }
      }
    });
    form.classList.toggle('is-submitting', on);

    if (cfg && cfg.isDlCoa) return;

    const host = getLoaderHost(form, cfg);
    const loader = ensureFormLoader(host);
    if (loader) {
      loader.classList.toggle('is-visible', on);
      loader.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
  }

  function buildJsonBody(form, source) {
    const fd = new FormData(form);
    const body = { source: source };

    fd.forEach(function (value, key) {
      if (key === 'g-recaptcha-response') {
        body['g-recaptcha-response'] = value;
        body.recaptcha_token = value;
        return;
      }
      if (key === 'website' || key === 'contact[website]') {
        body.website = value;
        return;
      }

      const m = key.match(/^contact\[(.+)\]$/);
      if (m) {
        body[m[1]] = value;
        return;
      }

      body[key] = value;
    });

    if (!body.message && body.body) body.message = body.body;
    if (!body.message && body.Notes) body.message = body.Notes;
    if (!body.body && body.message) body.body = body.message;
    if (!body.page_url && body['Page URL']) body.page_url = body['Page URL'];
    if (!body.phone && body.Phone) body.phone = body.Phone;
    if (!body.country && body.Country) body.country = body.Country;

    return body;
  }

  function executeRecaptcha(siteKey, action) {
    return new Promise(function (resolve, reject) {
      if (!siteKey) {
        reject(
          new Error('reCAPTCHA site key is not configured. Set Theme settings → API → reCAPTCHA v3 site key.')
        );
        return;
      }
      if (typeof grecaptcha === 'undefined') {
        reject(new Error('reCAPTCHA failed to load. Disable ad blockers and try again.'));
        return;
      }
      grecaptcha.ready(function () {
        grecaptcha
          .execute(siteKey, { action: action })
          .then(resolve)
          .catch(function () {
            reject(new Error('reCAPTCHA verification failed. Please try again.'));
          });
      });
    });
  }

  async function postToApi(form, cfg) {
    ensurePageUrlField(form);

    const tokenInput = form.querySelector('[data-recaptcha-token]');
    const token = await executeRecaptcha(cfg.siteKey, recaptchaAction(cfg.source));
    if (tokenInput) tokenInput.value = token;

    const body = buildJsonBody(form, cfg.source);
    body['g-recaptcha-response'] = token;
    body.recaptcha_token = token;

    const res = await fetch(cfg.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(function () {
      return {};
    });

    if (!res.ok || !data.success) {
      const serverMsg = data && data.message ? String(data.message) : '';
      const hint = res.status ? ' (HTTP ' + res.status + ')' : '';
      throw new Error(serverMsg || 'Could not send your message. Please try again.' + hint);
    }

    return data;
  }

  function bindForm(form) {
    if (form.dataset[BOUND_FLAG] === '1') return;
    if (!prepareForm(form)) return;

    const cfg = getConfig(form);
    if (!cfg.apiUrl) return;

    form.dataset[BOUND_FLAG] = '1';

    form.addEventListener(
      'submit',
      function (e) {
      e.preventDefault();
      e.stopPropagation();
      clearError(form);
      ensurePageUrlField(form);

      if (
        !cfg.siteKey &&
        (window.location.protocol === 'https:' || window.location.hostname !== 'localhost')
      ) {
        showError(
          form,
          'reCAPTCHA site key is not configured. Set Theme settings → API → reCAPTCHA v3 site key.'
        );
        return;
      }

      syncProductTypes(cfg.root);

      if (cfg.isDlCoa) {
        dlCoaSetLoading(form, true);
      } else {
        setSubmitting(form, true, cfg);
      }

      postToApi(form, cfg)
        .then(function () {
          return postToShopifyContact(form, cfg);
        })
        .then(function () {
          if (cfg.isDlCoa) {
            showDlCoaSuccess(form);
            return;
          }

          setSubmitting(form, false, cfg);
          showSuccess(cfg);

          if (!cfg.isModal && cfg.successParam) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.set(cfg.successParam, '1');
              history.replaceState(null, '', url.pathname + url.search + url.hash);
            } catch (_e) {
              /* noop */
            }
          }
        })
        .catch(function (err) {
          if (cfg.isDlCoa) {
            dlCoaSetLoading(form, false);
          } else {
            setSubmitting(form, false, cfg);
          }
          const msg = err && err.message ? err.message : 'Submission failed.';
          if (cfg.isQuoteModal) {
            const pageErr = document.getElementById('errorMessage');
            if (pageErr) {
              pageErr.textContent = msg;
              pageErr.classList.add('show');
            }
            const pageOk = document.getElementById('successMessage');
            if (pageOk) pageOk.classList.remove('show');
          }
          showError(form, msg);
        });
    },
      true
    );
  }

  function bindProductTypeCheckboxes() {
    const qrfRoot = document.getElementById('qrf-root');
    if (!qrfRoot) return;
    const boxes = qrfRoot.querySelectorAll('.js-qrf-product-type-cb');
    boxes.forEach(function (cb) {
      if (cb.dataset.ptBound === '1') return;
      cb.dataset.ptBound = '1';
      cb.addEventListener('change', function () {
        syncProductTypes(qrfRoot);
        cb.closest('.type-option')?.classList.toggle('checked', cb.checked);
      });
    });
    syncProductTypes(qrfRoot);
  }

  function init() {
    injectLoaderStyles();

    const forms = findFormsToBind().filter(function (form) {
      return !!resolveApiUrl(form);
    });
    if (!forms.length) return;

    stripContactRedirectParamsFromLocation();
    syncPageUrlFields();
    forms.forEach(bindForm);
    bindProductTypeCheckboxes();
    initChipPopupReset();
  }

  function scheduleInit() {
    init();
    window.setTimeout(init, 300);
    window.setTimeout(init, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }

  if (typeof MutationObserver !== 'undefined') {
    let timer;
    const observer = new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(init, 200);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
